/**
 * @fileoverview Prep notes — the writing an instructor does BEFORE a class,
 * and the one rule that decides when it stops being prep.
 *
 * A class session has two halves of the same document. Before it meets, what
 * you type is a plan: what to cover, which piece to start with, what the
 * organization asked for. Once it is under way, the same text is the record of
 * what happened. Hans does not make you write it twice — prep notes and lesson
 * notes are the same body field, and the only thing that changes at the
 * boundary is what the page calls it and what saving it means.
 *
 * **The boundary is five minutes before the scheduled start.** Not the start
 * itself: an instructor standing in the room at 3:29 for a 3:30 class is
 * teaching, not planning, and making them wait sixty seconds for the button to
 * change its mind is the software being pedantic about a clock. Not the end
 * either — a session that is running is being taught, and its notes are lesson
 * notes.
 *
 * **Prep notes are never evidence.** Nothing here says a class was taught: a
 * prep note writes no teaching record, consumes no usage, reaches no family and
 * counts towards nothing. It becomes a lesson only when an instructor saves one
 * against a session that has started, which is the moment the ordinary lesson
 * path runs with its ordinary evidence. A term's worth of plans typed in
 * August must never read as a term already taught.
 *
 * Two authors, never merged, each on the session row:
 *   `my_prep`    the instructor's own plan, which becomes the lesson body.
 *   `prep_note`  what the organization asked for (O7) — read-only here, never
 *                copied into the lesson, and never shown to a family.
 */

/** How long before a session's start prep turns into the lesson itself. */
export const PREP_LEAD_MINUTES = 5;

const PREP_LEAD_MS = PREP_LEAD_MINUTES * 60 * 1000;

/** A session's scheduled start as a Date, or null when the row carries none. */
export function sessionStart(session) {
  const raw = session?.start_at || session?.starts_at;
  if (raw) {
    const d = new Date(raw);
    if (!Number.isNaN(d.getTime())) return d;
  }
  // Fall back to the local date + clock the row always carries. Local, never
  // UTC: the session's time is stated in the place it is taught.
  const [y, m, day] = String(session?.date || "").split("-").map(Number);
  if (!y || !m || !day) return null;
  const [hh, mm] = String(session?.time_local || "").split(":").map(Number);
  return new Date(y, m - 1, day, Number.isFinite(hh) ? hh : 0, Number.isFinite(mm) ? mm : 0, 0, 0);
}

/**
 * Is this session still in its prep window?
 *
 * An undated session is not: there is no start to be five minutes ahead of,
 * and guessing "yes" would offer a plan for a class that may already be over.
 */
export function isPrepWindow(session, now = new Date()) {
  if (session?.status === "cancelled") return false;
  const start = sessionStart(session);
  if (!start) return false;
  return now.getTime() < start.getTime() - PREP_LEAD_MS;
}

/**
 * What one session is asking the instructor for, as a single word the whole
 * app can switch on. The page, the calendar and the composer must never
 * disagree about which of these a session is in.
 *
 *   cancelled  nothing — a snow day is part of the record and owes no notes
 *   written    the lesson exists; the row opens it
 *   prep       more than five minutes out; typing here is a plan
 *   open       started (or within five minutes of it) and not written up
 */
export function sessionState(session, now = new Date()) {
  if (session?.status === "cancelled") return "cancelled";
  if (session?.notes) return "written";
  return isPrepWindow(session, now) ? "prep" : "open";
}

/** Whether a session has anything prepared on it, by either author. */
export function hasPrep(session) {
  return Boolean(session?.my_prep?.body || session?.prep_note);
}

/**
 * The verb for the action on a session row.
 *
 * "Add prep notes" and "Write notes" are different promises — one is a plan
 * nobody is owed yet, the other is a record that is already late — so the
 * label changes with the window rather than staying a neutral "Notes".
 */
export function prepActionLabel(session, now = new Date()) {
  const state = sessionState(session, now);
  if (state === "written") return "Open notes";
  if (state !== "prep") return "Write notes";
  return session?.my_prep?.body ? "Edit prep notes" : "Add prep notes";
}
